import { useEffect, useState } from 'react';
import { Share, SquarePlus, X } from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'installPromptDismissed';
// Give the page a moment to render before sliding a banner over it.
const SHOW_DELAY_MS = 3000;

// Chrome's (non-standard, so untyped) install event.
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function loadDismissed(): boolean {
    try {
        return localStorage.getItem(STORAGE_KEY) !== null;
    } catch {
        return false;
    }
}

function saveDismissed(): void {
    try {
        localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
        // Storage disabled -- the banner just comes back next visit.
    }
}

// Below Tailwind's `sm` breakpoint, same as the rest of the app's mobile checks.
const isMobileViewport = () => window.matchMedia('(max-width: 639px)').matches;

const isStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

// iPadOS reports itself as a Mac, so fall back to touch support to catch it.
const isIOS = () =>
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);

// Mobile-only banner nudging visitors to add Ultiverse to their home screen.
// Android/Chrome exposes a real install prompt via `beforeinstallprompt`; iOS
// has no API, so there we show the Share -> Add to Home Screen instructions.
export default function InstallPrompt() {
    const [visible, setVisible] = useState(false);
    const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
    const ios = isIOS();

    useEffect(() => {
        if (loadDismissed() || isStandalone() || !isMobileViewport()) return;

        let timer: ReturnType<typeof setTimeout> | undefined;
        const show = () => {
            timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
        };

        const onBeforeInstall = (e: Event) => {
            e.preventDefault(); // suppress Chrome's own mini-infobar in favour of ours
            setInstallEvent(e as BeforeInstallPromptEvent);
            show();
        };
        const onInstalled = () => {
            saveDismissed();
            setVisible(false);
        };

        if (ios) show();
        window.addEventListener('beforeinstallprompt', onBeforeInstall);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('beforeinstallprompt', onBeforeInstall);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, [ios]);

    const dismiss = () => {
        saveDismissed();
        setVisible(false);
    };

    const install = async () => {
        if (!installEvent) return;
        await installEvent.prompt();
        await installEvent.userChoice;
        // The event is single-use; either way, don't nag again.
        setInstallEvent(null);
        dismiss();
    };

    if (!visible || (!ios && !installEvent)) return null;

    return (
        <div
            role="dialog"
            aria-label="Add Ultiverse to your home screen"
            className="fixed inset-x-0 bottom-0 z-[2147483647] p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] animate-in fade-in-0 slide-in-from-bottom-4">
            <div className="flex items-start gap-3 rounded-lg border bg-popover p-3 text-popover-foreground shadow-lg">
                <img
                    src="/apple-touch-icon.png"
                    alt=""
                    className="size-11 shrink-0 rounded-[10px] border"
                />
                <div className="min-w-0 flex-1 text-sm">
                    <p className="font-semibold">Get the Ultiverse app</p>
                    {ios ? (
                        <p className="mt-0.5 text-muted-foreground">
                            Tap{' '}
                            <Share
                                aria-label="Share"
                                className="inline size-4 -translate-y-px align-middle text-primary"
                            />{' '}
                            then{' '}
                            <span className="whitespace-nowrap font-medium text-foreground">
                                Add to Home Screen
                                <SquarePlus className="ml-1 inline size-4 -translate-y-px align-middle" />
                            </span>
                        </p>
                    ) : (
                        <>
                            <p className="mt-0.5 text-muted-foreground">
                                Add it to your home screen for quick access.
                            </p>
                            <Button size="sm" className="mt-2" onClick={install}>
                                Install
                            </Button>
                        </>
                    )}
                </div>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Dismiss"
                    className="-mr-1 -mt-1"
                    onClick={dismiss}>
                    <X />
                </Button>
            </div>
        </div>
    );
}
